import { apiClient } from './client';
import { User, UserCreate, UserUpdate, PaginatedResponse, PaginationParams } from '../../types/api';

export const usersApi = {
  /**
   * Get current user profile
   * @returns Current user
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/users/me');
    return response.data;
  },

  /**
   * Update current user profile
   * @param data - User update data
   * @returns Updated user
   */
  updateCurrentUser: async (data: UserUpdate): Promise<User> => {
    const response = await apiClient.put<User>('/users/me', data);
    return response.data;
  },

  /**
   * Get all users (admin only)
   * @param params - Pagination parameters
   * @returns Paginated list of users
   */
  getUsers: async (params?: PaginationParams): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/users/', {
      params: {
        skip: params?.skip || 0,
        limit: params?.limit || 100,
      },
    });
    return response.data;
  },

  /**
   * Create a new user (admin only)
   * @param data - User creation data
   * @returns Created user
   */
  createUser: async (data: UserCreate): Promise<User> => {
    const response = await apiClient.post<User>('/users/', data);
    return response.data;
  },

  /**
   * Get specific user by ID (admin only)
   * @param userId - User ID
   * @returns User
   */
  getUser: async (userId: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${userId}`);
    return response.data;
  },

  /**
   * Update specific user (admin only)
   * @param userId - User ID
   * @param data - User update data
   * @returns Updated user
   */
  updateUser: async (userId: string, data: UserUpdate): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${userId}`, data);
    return response.data;
  },

  /**
   * Delete specific user (admin only)
   * @param userId - User ID
   */
  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}`);
  },

  /**
   * Get all teachers
   * @param params - Pagination parameters
   * @returns Paginated list of teachers
   */
  getTeachers: async (params?: PaginationParams): Promise<PaginatedResponse<User>> => {
    const response = await apiClient.get<PaginatedResponse<User>>('/users/teachers', {
      params: {
        skip: params?.skip || 0,
        limit: params?.limit || 100,
      },
    });
    return response.data;
  },
};
